import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { DEFAULT_VIEWPORT_HEIGHT, MAX_AUTO_HEIGHT, viewportPinCss } from "./templateFrameViewport";

interface TemplateFrameProps {
  title: string;
  className?: string;
  style?: CSSProperties;
  /** Grow the frame to the height of its document instead of scrolling inside it. */
  autoHeight?: boolean;
  /**
   * Height the templates' screen-height utilities resolve to while `autoHeight` is on.
   * Inside the frame 100vh is the frame itself, so a `min-h-screen` hero would grow
   * with every fit (JUB-210); pinning it keeps the document height independent of the frame.
   */
  viewportHeight?: number;
  /** Called once the frame document is ready (for scrolling or measuring). */
  onReady?: (frame: HTMLIFrameElement) => void;
  "data-testid"?: string;
  children: ReactNode;
}

const COPIED_HEAD_NODES = 'link[rel="stylesheet"], link[rel="preconnect"], style';
const READY_ATTRIBUTE = "data-jb-template-frame";
const VIEWPORT_STYLE_ATTRIBUTE = "data-jb-frame-viewport";
const BASE_CSS = "html{overflow-x:hidden}html,body{margin:0;padding:0;min-height:100%}";

function copyHeadNode(target: Document, node: Node) {
  target.head.appendChild(target.importNode(node, true));
}
/**
 * JUB-205: a same-origin iframe that renders its children through a portal.
 * Media queries then resolve against the frame width, so a 364px "phone"
 * mock on a 1280px window shows the phone layout (an inline div still sees
 * the desktop breakpoints). The frame document receives a copy of every
 * stylesheet and font link of the host document and mirrors the ones added
 * later (lazy chunks); the portal keeps React context and event delegation.
 */
export function TemplateFrame({
  title,
  className,
  style,
  autoHeight = false,
  viewportHeight = DEFAULT_VIEWPORT_HEIGHT,
  onReady,
  children,
  ...rest
}: TemplateFrameProps) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  const [mountNode, setMountNode] = useState<HTMLElement | null>(null);

  useLayoutEffect(() => {
    const frame = frameRef.current;
    if (!frame) return undefined;
    let headObserver: MutationObserver | null = null;
    let sizeObserver: ResizeObserver | null = null;
    let notified = false;

    // Runs on mount, again when React re-runs the effect (StrictMode mounts twice in
    // development) and when the browser replaces the initial document on `load`. The
    // one-time part is guarded by the marker; the observers are (re)attached every time,
    // because the cleanup of the previous run disconnected them (JUB-210).
    const prepare = () => {
      const doc = frame.contentDocument;
      if (!doc?.body) return;
      if (!doc.body.hasAttribute(READY_ATTRIBUTE)) {
        doc.documentElement.lang = document.documentElement.lang;
        doc.documentElement.className = document.documentElement.className;
        const base = doc.createElement("style");
        base.textContent = BASE_CSS;
        doc.head.appendChild(base);
        document.head.querySelectorAll(COPIED_HEAD_NODES).forEach((node) => copyHeadNode(doc, node));
        doc.body.setAttribute(READY_ATTRIBUTE, "");
      }

      headObserver?.disconnect();
      headObserver = new MutationObserver((records) => {
        for (const record of records) {
          record.addedNodes.forEach((node) => {
            if (node instanceof Element && node.matches(COPIED_HEAD_NODES)) copyHeadNode(doc, node);
          });
        }
      });
      headObserver.observe(document.head, { childList: true });

      let pin = doc.head.querySelector<HTMLStyleElement>(`style[${VIEWPORT_STYLE_ATTRIBUTE}]`);
      if (autoHeight) {
        if (!pin) {
          pin = doc.createElement("style");
          pin.setAttribute(VIEWPORT_STYLE_ATTRIBUTE, "");
          doc.head.appendChild(pin);
        }
        pin.textContent = viewportPinCss(viewportHeight);
        const fit = () => {
          const next = Math.min(doc.documentElement.scrollHeight, MAX_AUTO_HEIGHT);
          if (next > 0 && frame.style.height !== `${next}px`) frame.style.height = `${next}px`;
        };
        // The observer must come from the frame's own window to watch its document.
        const FrameResizeObserver = (frame.contentWindow as (Window & { ResizeObserver?: typeof ResizeObserver }) | null)
          ?.ResizeObserver;
        if (typeof FrameResizeObserver === "function") {
          sizeObserver?.disconnect();
          const observer = new FrameResizeObserver(fit);
          observer.observe(doc.documentElement);
          observer.observe(doc.body);
          sizeObserver = observer;
        }
        fit();
      } else {
        pin?.remove();
      }

      setMountNode(doc.body);
      if (!notified) {
        notified = true;
        onReadyRef.current?.(frame);
      }
    };

    prepare();
    // Some browsers replace the initial about:blank document on load.
    frame.addEventListener("load", prepare);
    return () => {
      frame.removeEventListener("load", prepare);
      headObserver?.disconnect();
      sizeObserver?.disconnect();
    };
  }, [autoHeight, viewportHeight]);

  return (
    <>
      <iframe ref={frameRef} title={title} className={className} style={style} {...rest} />
      {mountNode ? createPortal(children, mountNode) : null}
    </>
  );
}
